/**
 * A group's permissions (D14): one checkbox per grantable permission, with its
 * human label and what it allows (`lib/admin/permissions.ts`). `locked` ids
 * cannot be unticked — the Admin group's admin permissions, which the server
 * keeps anyway.
 */
import type { PermissionId } from '@acp/api-contract/permissions.js';
import type { PermissionInfo } from '../../lib/admin/permissions.js';

export type PermissionChecklistProps = {
  readonly permissions: readonly PermissionInfo[];
  readonly selected: readonly PermissionId[];
  readonly locked: readonly PermissionId[];
  readonly disabled: boolean;
  readonly onChange: (selected: PermissionId[]) => void;
};

export function PermissionChecklist({ permissions, selected, locked, disabled, onChange }: PermissionChecklistProps) {
  return (
    <fieldset className="grid gap-3 sm:grid-cols-2" data-testid="permission-checklist">
      <legend className="mb-2 text-sm font-medium">Permissions</legend>
      {permissions.map((p) => {
        const isLocked = locked.includes(p.id);
        return (
          <label key={p.id} className="flex items-start gap-2 text-sm" data-permission={p.id}>
            <input
              type="checkbox"
              className="mt-1"
              checked={selected.includes(p.id)}
              disabled={disabled || isLocked}
              onChange={(e) => onChange(e.target.checked ? [...selected, p.id] : selected.filter((id) => id !== p.id))}
            />
            <span>
              <span className="font-medium">{p.label}</span>
              <span className="ml-1 font-mono text-xs text-muted-foreground">{p.id}</span>
              <span className="block text-xs text-muted-foreground">
                {p.description}
                {isLocked ? ' (kept by this group)' : ''}
              </span>
            </span>
          </label>
        );
      })}
    </fieldset>
  );
}
